import React,{useState,useEffect,useMemo} from 'react';
import {useNavigate} from 'react-router-dom';
import {demoRepository} from '../../repository/demoRepository';
import SafeIcon from '../../common/SafeIcon';

const GlobalCommandPalette=()=> {
  const navigate=useNavigate();
  const [isOpen,setIsOpen]=useState(false);
  const [query,setQuery]=useState('');

  const getCurrentPropertyIdFromPath=()=> {
    try {
      const match=window.location.pathname.match(/\/properties\/([^/?#]+)/);
      return match?.[1] ? decodeURIComponent(match[1]) : '';
    } catch (error) {
      return '';
    }
  };

  const searchResults=useMemo(()=> {
    const lowerQuery=query.toLowerCase();
    const results=[];
    const currentPropertyId=getCurrentPropertyIdFromPath();

    const getRepoList=(getterName)=> {
      try {
        const getter=demoRepository?.[getterName];
        const value=typeof getter==='function' ? getter() : getter;
        return Array.isArray(value) ? value : [];
      } catch (error) {
        return [];
      }
    };

    const toSearchText=(values)=> {
      return values
        .flatMap(value=> Array.isArray(value) ? value : [value])
        .filter(value=> value !==undefined && value !==null)
        .map(value=> {
          if (typeof value==='object') {
            try {
              return JSON.stringify(value);
            } catch (error) {
              return '';
            }
          }
          return String(value);
        })
        .join(' ')
        .toLowerCase();
    };

    const expandDocumentQuery=(rawQuery)=> {
      const base=String(rawQuery || '').trim().toLowerCase();

      if (!base) return [];

      const synonymMap={
        insurance: ['insurance','pds','policy','certificate','coverage','qbe','landlord insurance','building insurance'],
        pds: ['pds','insurance','policy','product disclosure statement'],
        certificate: ['certificate','insurance','occupancy','final certificate'],
        warranty: ['warranty','manual','defect','maintenance','appliance'],
        defect: ['defect','defects','inspection','issue','repair','maintenance','condition report'],
        inspection: ['inspection','defect','condition','entry condition','exit condition','report'],
        tax: ['tax','depreciation','tax depreciation','capital works','accountant','statement','schedule'],
        depreciation: ['depreciation','tax','capital allowance','capital works','schedule'],
        floor: ['floor','floor plan','plan','layout'],
        lease: ['lease','tenant','rent','agreement','occupancy'],
        statement: ['statement','owner statement','agent statement','bank statement','loan statement','financial year'],
        loan: ['loan','interest','refinance','bank statement'],
        council: ['council','rates','notice'],
        water: ['water','bill','notice'],
        strata: ['strata','levy','owners corporation'],
        settlement: ['settlement','contract','sale','purchase'],
        contract: ['contract','sale','building contract','agreement'],
        quote: ['quote','repair','maintenance','estimate'],
        invoice: ['invoice','tax invoice','repair invoice','bill']
      };

      const words=base.split(/\s+/).filter(Boolean);
      const expanded=new Set([base,...words]);

      words.forEach(word=> {
        (synonymMap[word] || []).forEach(term=> expanded.add(term));
      });

      Object.entries(synonymMap).forEach(([key,terms])=> {
        if (base.includes(key)) {
          terms.forEach(term=> expanded.add(term));
        }
      });

      return Array.from(expanded);
    };

    const expandQueryTerms=(rawQuery)=> {
      const base=String(rawQuery || '').trim().toLowerCase();
      if (!base) return [];
      const synonymMap={
        defect: ['defect','defects','inspection','issue','issues','repair','maintenance','condition','report'],
        defects: ['defect','defects','inspection','issue','issues','repair','maintenance','condition','report'],
        issue: ['issue','issues','defect','repair','maintenance','open item'],
        repair: ['repair','maintenance','invoice','quote','defect','issue'],
        tax: ['tax','depreciation','tax depreciation','accountant','ato','statement','report','schedule'],
        depreciation: ['depreciation','tax','tax depreciation','capital works','schedule'],
        floor: ['floor','floor plan','plan','layout'],
        plan: ['plan','floor plan','survey','zoning'],
        lease: ['lease','tenant','rent','rental','agreement'],
        rent: ['rent','rental','lease','tenant','arrears'],
        insurance: ['insurance','certificate','policy','pds','building insurance'],
        statement: ['statement','owner statement','agent statement','bank statement','loan statement','financial year'],
        bank: ['bank','statement','loan','interest'],
        loan: ['loan','interest','refinance','bank','statement'],
        council: ['council','rates','notice'],
        water: ['water','bill','notice'],
        strata: ['strata','levy','owners corporation'],
        valuation: ['valuation','property valuation','refinance'],
        settlement: ['settlement','contract','sale','purchase'],
        contract: ['contract','sale','building contract','agreement'],
        tenant: ['tenant','lease','rent','occupant'],
        unit: ['unit','space','room','flat','apartment','granny flat'],
        space: ['space','unit','room','flat','apartment','granny flat'],
        urgent: ['urgent','high','priority','overdue','open']
      };
      const words=base.split(/\s+/).filter(Boolean);
      const expanded=new Set([base,...words]);
      words.forEach(word=> {
        (synonymMap[word] || []).forEach(term=> expanded.add(term));
      });
      Object.entries(synonymMap).forEach(([key,terms])=> {
        if (base.includes(key)) {
          terms.forEach(term=> expanded.add(term));
        }
      });
      return Array.from(expanded);
    };

    const scoreSearchMatch=(item,rawQuery)=> {
      const q=String(rawQuery || '').trim().toLowerCase();
      if (!q) return item.defaultScore || 1;
      const terms=expandQueryTerms(q);
      const title=String(item.title || '').toLowerCase();
      const subtitle=String(item.subtitle || '').toLowerCase();
      const type=String(item.label || item.type || '').toLowerCase();
      const searchText=String(item.searchText || '').toLowerCase();
      let score=0;
      if (title===q) score +=120;
      if (title.startsWith(q)) score +=90;
      if (title.includes(q)) score +=70;
      if (subtitle.includes(q)) score +=40;
      if (type.includes(q)) score +=25;
      if (searchText.includes(q)) score +=35;
      terms.forEach(term=> {
        if (!term) return;
        if (title.includes(term)) score +=35;
        if (subtitle.includes(term)) score +=20;
        if (searchText.includes(term)) score +=12;
      });
      if (item.type==='property') score +=12;
      if (item.type==='space') score +=10;
      if (item.type==='document') score +=8;
      if (item.type==='openItem') score +=6;
      const statusText=searchText + ' ' + subtitle;
      if (statusText.includes('archived') || statusText.includes('superseded') || statusText.includes('closed')) {
        score -=25;
      }
      return score;
    };

    const getPropertyDisplayName=(property)=> {
      if (!property) return '';
      return property.nickname || property.nickName || property.displayName || property.name || property.propertyName || property.address || property.addressLine1 || 'Unnamed property';
    };

    const getPropertyAddressText=(property)=> {
      if (!property) return '';
      return [
        property.address,
        property.addressLine1,
        property.suburb,
        property.state,
        property.postcode
      ].filter(Boolean).join(', ');
    };

    const getSpaceDisplayName=(space)=> {
      if (!space) return '';
      return space.name || space.spaceName || space.unitName || space.unitNumber || space.label || space.title || 'Unnamed space';
    };

    const getSpacePropertyId=(space)=> {
      return space.propertyId || space.property || space.property_id || space.linkedPropertyId || space.metadata?.propertyId || space.metadata?.property || space.extraData?.propertyId || space.extraData?.property || '';
    };

    const getDocumentPropertyId=(doc)=> {
      return doc.property || doc.propertyId || doc.property_id || doc.linkedPropertyId || doc.metadata?.propertyId || doc.metadata?.property || doc.extraData?.propertyId || doc.extraData?.property || '';
    };

    const getDocumentSpaceId=(doc)=> {
      return doc.space || doc.spaceId || doc.space_id || doc.rentableSpace || doc.rentableSpaceId || doc.metadata?.spaceId || doc.metadata?.space || doc.metadata?.rentableSpaceId || doc.extraData?.spaceId || doc.extraData?.space || doc.extraData?.rentableSpaceId || '';
    };

    const getLinkedPropertyId=(record)=> {
      return record.propertyId || record.property || record.property_id || record.linkedPropertyId || record.metadata?.propertyId || record.metadata?.property || record.extraData?.propertyId || record.extraData?.property || '';
    };

    const getLinkedSpaceId=(record)=> {
      return record.spaceId || record.space || record.space_id || record.rentableSpace || record.rentableSpaceId || record.metadata?.spaceId || record.metadata?.space || record.metadata?.rentableSpaceId || record.extraData?.spaceId || record.extraData?.space || record.extraData?.rentableSpaceId || '';
    };

    // Build shared data and lookup maps once
    const properties=getRepoList('getAllProperties');
    const spaces=getRepoList('getAllSpaces');
    const tenants=getRepoList('getAllTenants');
    const docs=getRepoList('getDocuments');
    const openItems=getRepoList('getAllOpenItems');

    const propertyById=new Map(
      properties
        .filter(p=> p?.id)
        .map(p=> [String(p.id),p])
    );

    const spaceById=new Map(
      spaces
        .filter(s=> s?.id)
        .map(s=> [String(s.id),s])
    );

    // Search pages
    const pages=[
      {type: 'page',title: 'Dashboard',path: '/',icon: 'Grid'},
      {type: 'page',title: 'Properties',path: '/properties',icon: 'Home'},
      {type: 'page',title: 'Tenants',path: '/tenants',icon: 'Users'},
      {type: 'page',title: 'Rent Schedules',path: '/rent-schedules',icon: 'Calendar'},
      {type: 'page',title: 'Transactions',path: '/transactions',icon: 'DollarSign'},
      {type: 'page',title: 'Insurance',path: '/insurance',icon: 'Shield'},
      {type: 'page',title: 'Documents',path: '/documents',icon: 'FileText'},
      {type: 'page',title: 'Inspections',path: '/inspections',icon: 'CheckSquare'},
      {type: 'page',title: 'Open Items',path: '/open-items',icon: 'AlertCircle'},
      {type: 'page',title: 'Reports',path: '/reports',icon: 'PieChart'},
      {type: 'page',title: 'Settings',path: '/settings',icon: 'Settings'}
    ];

    pages.forEach(page=> {
      const item={...page,label: 'Page',actionLabel: 'Open page',searchText: toSearchText([page.title,page.path]),defaultScore: 2};
      const score=scoreSearchMatch(item,query);
      if (!query.trim() || score > 0) {
        results.push({...item,score});
      }
    });

    // Search properties
    properties.forEach(prop=> {
      const propertyDisplayName=getPropertyDisplayName(prop);
      const propertyAddressText=getPropertyAddressText(prop);
      const searchStr=toSearchText([
        propertyDisplayName,propertyAddressText,prop.nickname,prop.nickName,prop.displayName,prop.name,
        prop.propertyName,prop.address,prop.addressLine1,prop.suburb,prop.state,prop.postcode,
        prop.propertyType,prop.status,prop.metadata,prop.extraData
      ]);
      const item={
        type: 'property',label: 'Property',title: propertyDisplayName,
        subtitle: propertyAddressText || [prop.suburb,prop.state,prop.postcode].filter(Boolean).join(', ') || prop.propertyType || 'Property',
        path: `/properties/${prop.id}`,icon: 'Home',actionLabel: 'Open property',searchText: searchStr
      };
      const score=scoreSearchMatch(item,query);
      if (!query.trim() || score > 0) {
        results.push({...item,score});
      }
    });

    // Search spaces
    spaces.forEach(space=> {
      const linkedPropertyId=getSpacePropertyId(space);
      const linkedProperty=linkedPropertyId ? propertyById.get(String(linkedPropertyId)) : null;
      const spaceDisplayName=getSpaceDisplayName(space);
      const linkedPropertyName=getPropertyDisplayName(linkedProperty);
      const linkedPropertyAddress=getPropertyAddressText(linkedProperty);
      const searchStr=toSearchText([
        spaceDisplayName,space.name,space.spaceName,space.unitName,space.unitNumber,space.label,
        space.title,space.status,space.occupancyStatus,space.spaceType,space.type,
        linkedPropertyName,linkedPropertyAddress,linkedProperty?.nickname,linkedProperty?.nickName,
        linkedProperty?.name,linkedProperty?.address,linkedProperty?.suburb,linkedProperty?.state,
        linkedProperty?.postcode,space.metadata,space.extraData
      ]);
      const item={
        type: 'space',label: 'Space',title: spaceDisplayName,
        subtitle: [
          linkedPropertyName || linkedPropertyAddress || 'Linked property',
          space.status || space.occupancyStatus || space.spaceType || space.type
        ].filter(Boolean).join(' • '),
        path: linkedPropertyId ? `/properties/${linkedPropertyId}` : '/properties',
        icon: 'Building2',actionLabel: 'Open linked property',searchText: searchStr
      };
      const score=scoreSearchMatch(item,query);
      if (!query.trim() || score > 0) {
        results.push({...item,score});
      }
    });

    // Search tenants
    tenants.forEach(tenant=> {
      const linkedPropertyId=getLinkedPropertyId(tenant);
      const linkedProperty=linkedPropertyId ? propertyById.get(String(linkedPropertyId)) : null;
      const linkedPropertyName=getPropertyDisplayName(linkedProperty);
      const linkedSpaceId=getLinkedSpaceId(tenant);
      const linkedSpace=linkedSpaceId ? spaceById.get(String(linkedSpaceId)) : null;
      const linkedSpaceName=getSpaceDisplayName(linkedSpace);
      const searchStr=toSearchText([
        tenant.firstName,tenant.lastName,tenant.name,tenant.fullName,tenant.email,tenant.phone,
        tenant.status,linkedPropertyName,linkedSpaceName,tenant.metadata
      ]);
      const item={
        type: 'tenant',label: 'Tenant',
        title: tenant.name || tenant.fullName || [tenant.firstName,tenant.lastName].filter(Boolean).join(' ') || tenant.email || 'Unnamed tenant',
        subtitle: linkedPropertyName || tenant.email || tenant.phone || tenant.status || 'Tenant',
        path: `/tenants/${tenant.id}`,icon: 'User',actionLabel: 'Open tenant',searchText: searchStr
      };
      const score=scoreSearchMatch(item,query);
      if (!query.trim() || score > 0) {
        results.push({...item,score});
      }
    });

    // Search documents
    docs.forEach(doc=> {
      const linkedPropertyId=getDocumentPropertyId(doc);
      const linkedProperty=linkedPropertyId ? propertyById.get(String(linkedPropertyId)) : null;

      const linkedSpaceId=getDocumentSpaceId(doc);
      const linkedSpace=linkedSpaceId ? spaceById.get(String(linkedSpaceId)) : null;

      const linkedPropertyName=getPropertyDisplayName(linkedProperty) || doc.propertyName || doc.propertyLabel || doc.metadata?.propertyName || doc.extraData?.propertyName || '';
      const linkedPropertyAddress=getPropertyAddressText(linkedProperty);
      const linkedSpaceName=getSpaceDisplayName(linkedSpace) || doc.spaceName || doc.spaceLabel || doc.metadata?.spaceName || doc.extraData?.spaceName || '';

      const documentTitle=doc.title || doc.fileName || (Array.isArray(doc.fileNames) ? doc.fileNames[0] : '') || 'Untitled document';

      const documentSubtitle=[
        linkedPropertyName || linkedPropertyAddress || 'No linked property',
        linkedSpaceName,
        doc.documentType,
        doc.status
      ].filter(Boolean).join(' • ') || 'Document';

      const documentSearchText=toSearchText([
        documentTitle,
        doc.title,
        doc.fileName,
        doc.fileNames,
        doc.files,
        doc.fileType,
        doc.documentType,
        doc.status,
        doc.notes,
        doc.description,
        doc.adminNotes,
        doc.tags,
        doc.uploadedDate,
        doc.documentDate,
        doc.propertyName,
        doc.propertyLabel,
        linkedPropertyName,
        linkedPropertyAddress,
        linkedProperty?.nickname,
        linkedProperty?.nickName,
        linkedProperty?.name,
        linkedProperty?.propertyName,
        linkedProperty?.address,
        linkedProperty?.addressLine1,
        linkedProperty?.suburb,
        linkedProperty?.state,
        linkedProperty?.postcode,
        linkedSpaceName,
        linkedSpace?.name,
        linkedSpace?.spaceName,
        linkedSpace?.unitName,
        linkedSpace?.unitNumber,
        linkedSpace?.label,
        doc.metadata,
        doc.extraData
      ]);

      const queryTerms=expandDocumentQuery(query);
      const directMatch=!query.trim() || documentSearchText.includes(query.trim().toLowerCase());
      const synonymMatch=queryTerms.some(term=> documentSearchText.includes(term));

      let score=0;

      if (!query.trim()) {
        score=1;
      } else {
        if (documentTitle.toLowerCase() === query.trim().toLowerCase()) score += 120;
        if (documentTitle.toLowerCase().startsWith(query.trim().toLowerCase())) score += 90;
        if (documentTitle.toLowerCase().includes(query.trim().toLowerCase())) score += 70;
        if (documentSubtitle.toLowerCase().includes(query.trim().toLowerCase())) score += 45;
        if (documentSearchText.includes(query.trim().toLowerCase())) score += 35;
        if (synonymMatch) score += 25;
        if (currentPropertyId && String(linkedPropertyId)===String(currentPropertyId)) score += 60;
        if (documentSearchText.includes('archived') || documentSearchText.includes('superseded')) score -= 30;
      }

      if (directMatch || synonymMatch || (!query.trim() && currentPropertyId && String(linkedPropertyId)===String(currentPropertyId))) {
        results.push({
          id: `document-${doc.id}`,
          type: 'document',
          label: currentPropertyId && String(linkedPropertyId)===String(currentPropertyId) ? 'Current Property Document' : 'Document',
          title: documentTitle,
          subtitle: documentSubtitle,
          path: '/documents',
          icon: 'FileText',
          actionLabel: 'Open Documents',
          score,
          searchText: documentSearchText
        });
      }
    });

    // Search open items
    openItems.forEach(oi=> {
      const linkedPropertyId=getLinkedPropertyId(oi);
      const linkedProperty=linkedPropertyId ? propertyById.get(String(linkedPropertyId)) : null;
      const linkedPropertyName=getPropertyDisplayName(linkedProperty);
      const linkedPropertyAddress=getPropertyAddressText(linkedProperty);
      const linkedSpaceId=getLinkedSpaceId(oi);
      const linkedSpace=linkedSpaceId ? spaceById.get(String(linkedSpaceId)) : null;
      const linkedSpaceName=getSpaceDisplayName(linkedSpace);
      const searchStr=toSearchText([
        oi.title,oi.summary,oi.description,oi.category,oi.priority,oi.status,oi.notes,
        linkedPropertyName,linkedPropertyAddress,linkedSpaceName,oi.metadata,oi.extraData
      ]);
      const oiItem={
        type: 'openItem',label: 'Open Item',title: oi.title,
        subtitle: [
          linkedPropertyName || linkedPropertyAddress,
          linkedSpaceName,
          oi.priority,
          oi.status
        ].filter(Boolean).join(' • ') || 'Open Item',
        path: '/open-items',icon: 'AlertCircle',actionLabel: 'Open Items',searchText: searchStr
      };
      const score=scoreSearchMatch(oiItem,query);
      if (!query.trim() || score > 0) {
        results.push({...oiItem,score});
      }
    });

    return results
      .filter(item=> !query.trim() || (item.score || 0) > 0)
      .sort((a,b)=> (b.score || 0) - (a.score || 0))
      .slice(0,20);
  },[query]);

  // Keyboard shortcuts
  useEffect(()=> {
    const handleKeyDown=(e)=> {
      if ((e.ctrlKey || e.metaKey) && (e.key==='k' || e.key==='K')) {
        e.preventDefault();
        setIsOpen(true);
      }
      if (isOpen && e.key==='Escape') {
        e.preventDefault();
        setIsOpen(false);
      }
      if (isOpen && e.key==='Enter') {
        e.preventDefault();
        if (searchResults.length > 0) {
          const firstResult=searchResults[0];
          if (firstResult.type==='document') {
            navigate('/documents');
            window.setTimeout(()=> window.dispatchEvent(new CustomEvent('mpp:documents-filter',{detail: {query: firstResult.title}})),0);
          } else {
            navigate(firstResult.path);
          }
          setIsOpen(false);
        }
      }
    };
    window.addEventListener('keydown',handleKeyDown);
    return ()=> window.removeEventListener('keydown',handleKeyDown);
  },[isOpen,searchResults,navigate]);

  const handleNavigate=(result)=> {
    if (result.type==='document') {
      navigate('/documents');
      window.setTimeout(()=> window.dispatchEvent(new CustomEvent('mpp:documents-filter',{detail: {query: result.title}})),0);
    } else {
      navigate(result.path);
    }
    setIsOpen(false);
  };

  const handleClose=()=> {
    setIsOpen(false);
    setQuery('');
  };

  const getIcon=(iconName)=> {
    const iconMap={
      'Grid': 'Grid',
      'Home': 'Home',
      'Users': 'Users',
      'Calendar': 'Calendar',
      'DollarSign': 'DollarSign',
      'Shield': 'Shield',
      'FileText': 'FileText',
      'CheckSquare': 'CheckSquare',
      'AlertCircle': 'AlertCircle',
      'PieChart': 'PieChart',
      'Settings': 'Settings',
      'User': 'User',
      'Layers': 'Layers',
      'Building2': 'Building2'
    };
    return iconMap[iconName] || 'Grid';
  };

  const typeLabels={
    page: 'Page',
    property: 'Property',
    space: 'Space',
    tenant: 'Tenant',
    document: 'Document',
    openItem: 'Open Item'
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-4 sm:pt-24 px-3 sm:px-4 pointer-events-none">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={handleClose} />

      {/* Modal */}
      <div className="relative w-full max-w-2xl max-h-[calc(100dvh-2rem)] bg-white rounded-2xl shadow-2xl pointer-events-auto animate-scale-in overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b border-slate-100 bg-white sticky top-0 z-10">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
              <SafeIcon icon="Search" className="text-slate-500" />
            </div>
            <div className="min-w-0">
              <h2 className="text-base sm:text-lg font-semibold text-slate-900">Smart Search</h2>
              <p className="text-[11px] sm:text-xs text-slate-500 truncate">Properties, spaces, tenants, documents, open items and pages</p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors flex-shrink-0"
            title="Close (Esc)"
          >
            <SafeIcon icon="X" className="text-lg" />
          </button>
        </div>

        {/* Search Input */}
        <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-slate-100 bg-white z-10">
          <div className="relative">
            <SafeIcon icon="Search" className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={query}
              onChange={(e)=> setQuery(e.target.value)}
              placeholder="Search the entire portfolio..."
              className="w-full pl-11 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
              autoFocus
              autoComplete="off"
            />
          </div>
        </div>

        {/* Results */}
        <div className="flex-1 min-h-0 overflow-y-auto">
          {query.trim().length < 2 ? (
            <div className="p-6 sm:p-8 text-center">
              <SafeIcon icon="Search" className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-sm text-slate-500">Type to search across the entire portfolio</p>
              <div className="hidden sm:flex justify-center gap-2 mt-4">
                <div className="px-3 py-1.5 bg-slate-100 text-xs text-slate-600 rounded-lg">Ctrl</div>
                <div className="px-3 py-1.5 bg-slate-100 text-xs text-slate-600 rounded-lg">K</div>
              </div>
            </div>
          ) : searchResults.length===0 ? (
            <div className="p-8 text-center">
              <SafeIcon icon="Inbox" className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-sm text-slate-500">No results found for "{query}"</p>
              <button type="button" onClick={handleClose} className="text-amber-600 hover:underline text-xs">
                Clear search
              </button>
            </div>
          ) : (
            <div className="p-2">
              <div className="text-xs text-slate-400 uppercase tracking-wide font-semibold px-4 py-2 sticky top-0 bg-white">
                {searchResults.length} result{searchResults.length !==1 ? 's' : ''} found
              </div>
              <div className="space-y-1">
                {searchResults.map((result)=> (
                  <button
                    key={result.id || `${result.type}-${result.path}-${result.title}`}
                    onClick={()=> handleNavigate(result)}
                    onKeyDown={(e)=> {
                      if (e.key==='Enter') {
                        handleNavigate(result);
                      }
                    }}
                    className="w-full text-left group hover:bg-slate-50 rounded-lg transition-colors"
                  >
                    <div className="flex items-start gap-3 p-3">
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0 text-slate-600">
                        <SafeIcon icon={getIcon(result.icon)} className="text-lg" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-slate-900">{result.title}</div>
                        <div className="text-xs text-slate-500 mt-0.5 truncate">{result.subtitle}</div>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-[11px] text-slate-400">{result.label || typeLabels[result.type] || result.type}</span>
                          {result.actionLabel && (
                            <span className="text-[11px] text-amber-500 font-medium">{result.type==='document' ? 'Show document' : result.actionLabel}</span>
                          )}
                        </div>
                      </div>
                      <SafeIcon icon="ChevronRight" className="shrink-0 text-slate-300 group-hover:text-slate-600" />
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="hidden sm:block px-6 py-3 border-t border-slate-100 bg-white">
          <div className="flex justify-between items-center text-xs text-slate-400">
            <div>
              <span className="inline-flex items-center gap-1">
                <span className="font-medium text-slate-500">Press</span>
                <kbd className="px-2 py-1 bg-slate-100 border border-slate-300 rounded text-xs font-mono">Ctrl</kbd>
                <kbd className="px-2 py-1 bg-slate-100 border border-slate-300 rounded text-xs font-mono">K</kbd>
                <span className="font-medium text-slate-500">to open</span>
              </span>
            </div>
            <div>
              <span className="inline-flex items-center gap-1">
                <span className="font-medium text-slate-500">Press</span>
                <kbd className="px-2 py-1 bg-slate-100 border border-slate-300 rounded text-xs font-mono">Esc</kbd>
                <span className="font-medium text-slate-500">to close</span>
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GlobalCommandPalette;